import {
  Avatar,
  Button,
  Card,
  CardContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuItemIconSlot,
  DropdownMenuItemText,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tag,
  Typography,
  useLocaleFormat,
} from '@eye-able/ui';
import {
  Archive,
  ArchiveRestore,
  Copy,
  EllipsisVertical,
  ListChecks,
  Pencil,
  Trash2,
} from 'lucide-react';
import { IconZoomExclamation } from '@tabler/icons-react';
import { Link as RouterLink } from 'react-router-dom';
import { useState } from 'react';
import type { PolicyResponse } from '@http/queries/useFetchPolicies';
import { useFetchTeams } from '@http/queries';
import { PolicyRuleDialog } from './PolicyRuleDialog';
import { getInitials } from '@helpers/functions';
import { userAtom } from '@/global-store';
import { useAtomValue } from 'jotai';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { getPriorityTranslationKey } from '../priority';

function getTagVariant(value: string) {
  if (value === 'required' || value === 'active') return 'success';
  if (value === 'unwanted' || value === 'high') return 'warning';
  if (value === 'matches' || value === 'medium') return 'hint';
  if (value === 'archived') return 'error';
  return 'default';
}

function PolicyRowActions({
  policy,
  onDelete,
  onArchive,
  onUnarchive,
}: {
  policy: PolicyResponse;
  onDelete: (policy: PolicyResponse) => void;
  onArchive?: (policy: PolicyResponse) => void;
  onUnarchive?: (policy: PolicyResponse) => void;
}) {
  const { t } = useInlineTranslation();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant='ghost'
          size='icon-xs'
          aria-label={t('actionsForPolicy', { policyName: policy.name })}
        >
          <EllipsisVertical aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='eye:min-w-[180px]'>
        <DropdownMenuItem
          render={<RouterLink to={`/policies/${policy.id}/edit`} />}
        >
          <DropdownMenuItemIconSlot>
            <Pencil aria-hidden className='eye:size-16' />
          </DropdownMenuItemIconSlot>
          <DropdownMenuItemText>{t('editPolicy')}</DropdownMenuItemText>
        </DropdownMenuItem>
        <DropdownMenuItem
          render={<RouterLink to={`/policies/new?duplicateId=${policy.id}`} />}
        >
          <DropdownMenuItemIconSlot>
            <Copy aria-hidden className='eye:size-16' />
          </DropdownMenuItemIconSlot>
          <DropdownMenuItemText>{t('duplicate')}</DropdownMenuItemText>
        </DropdownMenuItem>
        {onArchive ? (
          <DropdownMenuItem onClick={() => onArchive(policy)}>
            <DropdownMenuItemIconSlot>
              <Archive aria-hidden className='eye:size-16' />
            </DropdownMenuItemIconSlot>
            <DropdownMenuItemText>{t('archive')}</DropdownMenuItemText>
          </DropdownMenuItem>
        ) : null}
        {onUnarchive ? (
          <DropdownMenuItem onClick={() => onUnarchive(policy)}>
            <DropdownMenuItemIconSlot>
              <ArchiveRestore aria-hidden className='eye:size-16' />
            </DropdownMenuItemIconSlot>
            <DropdownMenuItemText>{t('unarchive')}</DropdownMenuItemText>
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => onDelete(policy)}
          className='eye:text-red-100 eye:data-[highlighted]:bg-red-25 eye:data-[highlighted]:text-red-100'
        >
          <DropdownMenuItemIconSlot>
            <Trash2 aria-hidden className='eye:size-16' />
          </DropdownMenuItemIconSlot>
          <DropdownMenuItemText>{t('delete')}</DropdownMenuItemText>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function PolicyInventoryCard({
  policy,
  onDelete,
  onArchive,
  onUnarchive,
  canEdit,
}: {
  policy: PolicyResponse;
  onDelete: (policy: PolicyResponse) => void;
  onArchive?: (policy: PolicyResponse) => void;
  onUnarchive?: (policy: PolicyResponse) => void;
  canEdit: boolean;
}) {
  const { t } = useInlineTranslation();
  const { formatNumber } = useLocaleFormat();
  const assetTypeLabels: Record<PolicyResponse['assetType'], string> = {
    'html-pages': t('htmlPages'),
    documents: t('documents'),
    'all-assets': t('allAssets'),
  };
  const statusLabel = t(`policyStatus${policy.status}`);
  const violationLabel = t(`violationLabel${policy.violationLabel}`);
  const priorityLabel = t(getPriorityTranslationKey(policy.priority));
  const scopeLabel =
    policy.domainScope === 'all-domains'
      ? t('allDomains')
      : t('selectedDomains', {
          count: policy.auditEntryIds.length,
        });
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const user = useAtomValue(userAtom);
  const { data: teams = [] } = useFetchTeams(user?.primaryOrganisation);
  const team = teams.find((candidate) => candidate.id === policy.teamId);

  return (
    <Card className='eye:h-full'>
      <CardContent className='eye:flex eye:h-full eye:flex-col eye:gap-s eye:p-s'>
        <div className='eye:flex eye:items-start eye:justify-between eye:gap-s'>
          <div className='eye:min-w-0 eye:flex-1'>
            <Typography tag='h2' variant='headline-s'>
              {policy.name}
            </Typography>
            {policy.description ? (
              <Typography className='eye:mt-4xs eye:text-grey-120'>
                {policy.description}
              </Typography>
            ) : null}
          </div>
          {canEdit ? (
            <PolicyRowActions
              policy={policy}
              onDelete={onDelete}
              onArchive={onArchive}
              onUnarchive={onUnarchive}
            />
          ) : null}
        </div>

        <div className='eye:grid eye:grid-cols-1 eye:gap-xs eye:text-14 eye:text-grey-120 eye:md:grid-cols-3'>
          {/* "Unwanted" is what every policy the builder creates is, so
              showing it says nothing; only the inverted/other labels — which
              now only reach here via templates or older policies — are worth
              the row. */}
          {policy.violationLabel !== 'unwanted' ? (
            <div className='eye:flex eye:flex-col eye:gap-5xs'>
              <Typography variant='body-s' className='eye:text-grey-120'>
                {t('label')}
              </Typography>
              <Typography>{violationLabel}</Typography>
            </div>
          ) : null}
          <div className='eye:flex eye:flex-col eye:gap-5xs'>
            <Typography variant='body-s' className='eye:text-grey-120'>
              {t('priority')}
            </Typography>
            <Typography>{priorityLabel}</Typography>
          </div>
          <div className='eye:flex eye:flex-col eye:gap-5xs'>
            <Typography variant='body-s' className='eye:text-grey-120'>
              {t('status')}
            </Typography>
            <div>
              <Tag
                variant={getTagVariant(policy.status)}
                className='eye:min-w-0 eye:text-14'
              >
                {statusLabel}
              </Tag>
            </div>
          </div>
          <div className='eye:flex eye:flex-col eye:gap-5xs'>
            <Typography variant='body-s' className='eye:text-grey-120'>
              {t('type')}
            </Typography>
            <Typography>{assetTypeLabels[policy.assetType]}</Typography>
          </div>
          <div className='eye:flex eye:flex-col eye:gap-5xs'>
            <Typography variant='body-s' className='eye:text-grey-120'>
              {t('scope')}
            </Typography>
            <Typography>{scopeLabel}</Typography>
          </div>
          {team ? (
            <div className='eye:flex eye:flex-col eye:gap-5xs'>
              <Typography variant='body-s' className='eye:text-grey-120'>
                {t('team')}
              </Typography>
              <div className='eye:flex eye:items-center eye:gap-4xs'>
                <Avatar
                  style={{ backgroundColor: team.color || '#2B73D2' }}
                  className='eye:size-[24px] eye:shrink-0 eye:rounded-md eye:font-semibold eye:text-white'
                >
                  {getInitials(team.name)}
                </Avatar>
                <Typography>{team.name}</Typography>
              </div>
            </div>
          ) : null}
        </div>

        <div className='eye:mt-auto'>
          <div className='eye:flex eye:flex-wrap eye:justify-end eye:gap-xs'>
            <Button
              variant='outline'
              size='sm'
              onClick={() => setRuleDialogOpen(true)}
            >
              <ListChecks aria-hidden />
              {t('showRule')}
            </Button>
            {policy.openIssueCount ? (
              <Button asChild variant='black' size='sm'>
                <RouterLink to={`/governance/issues?policyId=${policy.id}`}>
                  <IconZoomExclamation aria-hidden />
                  {t('viewOpenIssues', {
                    count: policy.openIssueCount,
                    formattedCount: formatNumber(policy.openIssueCount),
                  })}
                </RouterLink>
              </Button>
            ) : null}
          </div>
        </div>
      </CardContent>

      <PolicyRuleDialog
        policy={ruleDialogOpen ? policy : null}
        onOpenChange={(open) => {
          if (!open) setRuleDialogOpen(false);
        }}
      />
    </Card>
  );
}

export default PolicyInventoryCard;
